import { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import { loginWithNostr, loginWithNsec, getToken, logout, authFetch } from './auth';
import {
  getTasks, createTask, updateTask, deleteTask,
  getFolders, createFolder, updateFolder, deleteFolder,
  getPriorities, createPriority, updatePriority, deletePriority,
} from './api';

// ── Constants ─────────────────────────────────────────────────────────────────
const STATUSES = ['New', 'Started', 'Paused', 'Complete', 'Deleted'];
const STATUS_CLASS = { New: 'sb-new', Started: 'sb-started', Paused: 'sb-paused', Complete: 'sb-complete', Deleted: 'sb-deleted' };
const STATUS_TOGGLE_CLASS = { New: 'st-new', Started: 'st-started', Paused: 'st-paused', Complete: 'st-complete', Deleted: 'st-deleted' };

// ── Helpers ───────────────────────────────────────────────────────────────────
function stripHtml(html) {
  return (html || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

function toDateStr(iso) {
  if (!iso) return '';
  return iso.slice(0, 10);
}

function fmtDate(iso) {
  const s = toDateStr(iso);
  if (!s) return '';
  const d = new Date(s + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function isOverdue(iso) {
  const s = toDateStr(iso);
  if (!s) return false;
  return new Date(s + 'T00:00:00') < new Date(new Date().toDateString());
}

function shortKey(pk) { return pk ? `${pk.slice(0, 8)}…${pk.slice(-4)}` : ''; }

function tagStyle(color) {
  return { background: color + '26', color, borderColor: color + '80' };
}

// ── Rich text editor ──────────────────────────────────────────────────────────
function RichTextEditor({ value, onChange, placeholder, minHeight = 100 }) {
  const editorRef = useRef(null);

  // Set initial content once on mount — never use dangerouslySetInnerHTML on a
  // contenteditable because React resets innerHTML on every render, jumping the cursor.
  useEffect(() => {
    if (editorRef.current) editorRef.current.innerHTML = value || '';
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exec = (cmd, arg = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
  };

  const handleInput = () => onChange(editorRef.current?.innerHTML || '');

  return (
    <div className="rich-editor">
      <div className="rich-toolbar">
        <button type="button" className="rich-btn" title="Bold"         onMouseDown={e => { e.preventDefault(); exec('bold'); }}><b>B</b></button>
        <button type="button" className="rich-btn" title="Italic"       onMouseDown={e => { e.preventDefault(); exec('italic'); }}><i>I</i></button>
        <button type="button" className="rich-btn" title="Underline"    onMouseDown={e => { e.preventDefault(); exec('underline'); }}><u>U</u></button>
        <span className="rich-sep" />
        <button type="button" className="rich-btn" title="Bullet list"  onMouseDown={e => { e.preventDefault(); exec('insertUnorderedList'); }}>• List</button>
        <button type="button" className="rich-btn" title="Ordered list" onMouseDown={e => { e.preventDefault(); exec('insertOrderedList'); }}>1. List</button>
        <span className="rich-sep" />
        <button type="button" className="rich-btn" title="Clear formatting" onMouseDown={e => { e.preventDefault(); exec('removeFormat'); }}>✕ Clear</button>
      </div>
      <div
        ref={editorRef}
        className={`rich-content${!stripHtml(value) ? ' is-empty' : ''}`}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        style={{ minHeight }}
        onInput={handleInput}
        onBlur={handleInput}
      />
    </div>
  );
}

// ── Login screen ──────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [tab,     setTab]     = useState('nostr');
  const [nsec,    setNsec]    = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const attempt = async fn => {
    setLoading(true); setError('');
    try { await fn(); onLogin(); }
    catch (e) { setError(e.message); }
    finally   { setLoading(false); }
  };

  return (
    <div className="login-screen">
      <div className="login-card">
        <h1>Task Manager</h1>
        <p>Sign in with your Nostr identity to continue.</p>
        <div className="login-tabs">
          <button className={tab === 'nostr' ? 'active' : ''} onClick={() => setTab('nostr')}>Alby / NIP-07</button>
          <button className={tab === 'nsec'  ? 'active' : ''} onClick={() => setTab('nsec')}>nsec key</button>
        </div>
        {tab === 'nostr' && (
          <>
            <p className="login-tab-body">Use the Alby browser extension or any NIP-07 compatible wallet.</p>
            <button className="btn-primary" style={{ width: '100%', padding: '10px' }}
              onClick={() => attempt(loginWithNostr)} disabled={loading}>
              {loading ? 'Connecting…' : 'Login with Nostr'}
            </button>
          </>
        )}
        {tab === 'nsec' && (
          <>
            <div className="nsec-warning">
              Your nsec is your Nostr private key. Only enter it here on your own server.
            </div>
            <input className="nsec-input" type="password" placeholder="nsec1…"
              value={nsec} onChange={e => setNsec(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && nsec && attempt(() => loginWithNsec(nsec))} autoFocus />
            <button className="btn-primary" style={{ width: '100%', padding: '10px' }}
              onClick={() => attempt(() => loginWithNsec(nsec))} disabled={loading || !nsec.trim()}>
              {loading ? 'Signing in…' : 'Login'}
            </button>
          </>
        )}
        {error && <div className="login-error">{error}</div>}
      </div>
    </div>
  );
}

// ── Folder Manager modal ──────────────────────────────────────────────────────
function FolderModal({ folders, onUpdate, onClose }) {
  const [list,    setList]    = useState(folders);
  const [editId,  setEditId]  = useState(null);
  const [eName,   setEName]   = useState('');
  const [eColor,  setEColor]  = useState('#888888');
  const [eErr,    setEErr]    = useState('');
  const [saving,  setSaving]  = useState(false);
  const [deleting,setDeleting]= useState(null);

  const startEdit = f => { setEditId(f.folder_id); setEName(f.name); setEColor(f.color); setEErr(''); };
  const startNew  = () => { setEditId('new'); setEName(''); setEColor('#4d9de0'); setEErr(''); };
  const cancelEdit = () => { setEditId(null); setEErr(''); };

  const saveEdit = async () => {
    if (!eName.trim()) return setEErr('Name is required');
    setSaving(true); setEErr('');
    try {
      if (editId === 'new') {
        const f = await createFolder({ name: eName.trim(), color: eColor, sort_order: list.length });
        const next = [...list, f]; setList(next); onUpdate(next);
      } else {
        const existing = list.find(f => f.folder_id === editId);
        const f = await updateFolder(editId, { name: eName.trim(), color: eColor, sort_order: existing?.sort_order ?? 0 });
        const next = list.map(x => x.folder_id === editId ? f : x); setList(next); onUpdate(next);
      }
      setEditId(null);
    } catch (e) { setEErr(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this folder? Tasks will lose this tag.')) return;
    setDeleting(id);
    try {
      await deleteFolder(id);
      const next = list.filter(f => f.folder_id !== id);
      setList(next); onUpdate(next);
      if (editId === id) setEditId(null);
    } catch (e) { alert(e.message); }
    finally { setDeleting(null); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()} style={{ width: 480 }}>
        <div className="modal-header">
          <h2>Manage Folders</h2>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="folder-list">
            {list.map(f => (
              <div key={f.folder_id}>
                {editId === f.folder_id ? (
                  <div className="folder-edit-row">
                    <input type="color" value={eColor} onChange={e => setEColor(e.target.value)} />
                    <input type="text" value={eName} onChange={e => setEName(e.target.value)}
                      placeholder="Folder name" autoFocus
                      onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }} />
                    <button className="btn-primary" style={{ padding: '5px 12px', fontSize: '0.82rem' }}
                      onClick={saveEdit} disabled={saving}>{saving ? '…' : 'Save'}</button>
                    <button className="btn-ghost" onClick={cancelEdit}>Cancel</button>
                  </div>
                ) : (
                  <div className="folder-list-item">
                    <span className="folder-swatch" style={{ background: f.color }} />
                    <span className="folder-name">{f.name}</span>
                    <button className="btn-icon" title="Edit" onClick={() => startEdit(f)}>✎</button>
                    <button className="btn-icon" title="Delete"
                      style={{ color: deleting === f.folder_id ? 'var(--s-deleted)' : undefined }}
                      onClick={() => handleDelete(f.folder_id)} disabled={deleting === f.folder_id}>✕</button>
                  </div>
                )}
                {editId === f.folder_id && eErr && <div className="folder-edit-err">{eErr}</div>}
              </div>
            ))}
          </div>
          <div className="new-folder-row">
            {editId === 'new' ? (
              <div>
                <div className="folder-edit-row">
                  <input type="color" value={eColor} onChange={e => setEColor(e.target.value)} />
                  <input type="text" value={eName} onChange={e => setEName(e.target.value)}
                    placeholder="New folder name" autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }} />
                  <button className="btn-primary" style={{ padding: '5px 12px', fontSize: '0.82rem' }}
                    onClick={saveEdit} disabled={saving}>{saving ? '…' : 'Add'}</button>
                  <button className="btn-ghost" onClick={cancelEdit}>Cancel</button>
                </div>
                {eErr && <div className="folder-edit-err">{eErr}</div>}
              </div>
            ) : (
              <button className="btn-ghost" style={{ width: '100%' }} onClick={startNew}>+ New Folder</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Priority Manager modal ────────────────────────────────────────────────────
function PriorityModal({ priorities, onUpdate, onClose }) {
  const [list,    setList]    = useState(priorities);
  const [editId,  setEditId]  = useState(null);
  const [eName,   setEName]   = useState('');
  const [eColor,  setEColor]  = useState('#888888');
  const [eErr,    setEErr]    = useState('');
  const [saving,  setSaving]  = useState(false);
  const [deleting,setDeleting]= useState(null);

  const startEdit = p => { setEditId(p.priority_id); setEName(p.name); setEColor(p.color); setEErr(''); };
  const startNew  = () => { setEditId('new'); setEName(''); setEColor('#e07b4d'); setEErr(''); };
  const cancelEdit = () => { setEditId(null); setEErr(''); };

  const saveEdit = async () => {
    if (!eName.trim()) return setEErr('Name is required');
    setSaving(true); setEErr('');
    try {
      if (editId === 'new') {
        const p = await createPriority({ name: eName.trim(), color: eColor, sort_order: list.length });
        const next = [...list, p]; setList(next); onUpdate(next);
      } else {
        const existing = list.find(p => p.priority_id === editId);
        const p = await updatePriority(editId, { name: eName.trim(), color: eColor, sort_order: existing?.sort_order ?? 0 });
        const next = list.map(x => x.priority_id === editId ? p : x); setList(next); onUpdate(next);
      }
      setEditId(null);
    } catch (e) { setEErr(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this priority? Tasks assigned to it will have no priority.')) return;
    setDeleting(id);
    try {
      await deletePriority(id);
      const next = list.filter(p => p.priority_id !== id);
      setList(next); onUpdate(next);
      if (editId === id) setEditId(null);
    } catch (e) { alert(e.message); }
    finally { setDeleting(null); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()} style={{ width: 480 }}>
        <div className="modal-header">
          <h2>Manage Priorities</h2>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="folder-list">
            {list.map(p => (
              <div key={p.priority_id}>
                {editId === p.priority_id ? (
                  <div className="folder-edit-row">
                    <input type="color" value={eColor} onChange={e => setEColor(e.target.value)} />
                    <input type="text" value={eName} onChange={e => setEName(e.target.value)}
                      placeholder="Priority name" autoFocus
                      onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }} />
                    <button className="btn-primary" style={{ padding: '5px 12px', fontSize: '0.82rem' }}
                      onClick={saveEdit} disabled={saving}>{saving ? '…' : 'Save'}</button>
                    <button className="btn-ghost" onClick={cancelEdit}>Cancel</button>
                  </div>
                ) : (
                  <div className="folder-list-item">
                    <span className="folder-swatch" style={{ background: p.color }} />
                    <span className="folder-name">{p.name}</span>
                    <button className="btn-icon" title="Edit" onClick={() => startEdit(p)}>✎</button>
                    <button className="btn-icon" title="Delete"
                      style={{ color: deleting === p.priority_id ? 'var(--s-deleted)' : undefined }}
                      onClick={() => handleDelete(p.priority_id)} disabled={deleting === p.priority_id}>✕</button>
                  </div>
                )}
                {editId === p.priority_id && eErr && <div className="folder-edit-err">{eErr}</div>}
              </div>
            ))}
          </div>
          <div className="new-folder-row">
            {editId === 'new' ? (
              <div>
                <div className="folder-edit-row">
                  <input type="color" value={eColor} onChange={e => setEColor(e.target.value)} />
                  <input type="text" value={eName} onChange={e => setEName(e.target.value)}
                    placeholder="New priority name" autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }} />
                  <button className="btn-primary" style={{ padding: '5px 12px', fontSize: '0.82rem' }}
                    onClick={saveEdit} disabled={saving}>{saving ? '…' : 'Add'}</button>
                  <button className="btn-ghost" onClick={cancelEdit}>Cancel</button>
                </div>
                {eErr && <div className="folder-edit-err">{eErr}</div>}
              </div>
            ) : (
              <button className="btn-ghost" style={{ width: '100%' }} onClick={startNew}>+ New Priority</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Task modal (create / edit) ────────────────────────────────────────────────
function TaskModal({ task, folders, priorities, onSave, onClose }) {
  const isNew = !task;
  const [name,        setName]        = useState(task?.name        ?? '');
  const [status,      setStatus]      = useState(task?.status      ?? 'New');
  const [targetDate,  setTargetDate]  = useState(task?.target_date ? toDateStr(task.target_date) : '');
  const [description, setDescription] = useState(task?.description ?? '');
  const [notes,       setNotes]       = useState(task?.notes       ?? '');
  const [folderIds,   setFolderIds]   = useState((task?.folders || []).map(f => f.folder_id));
  const [priorityId,  setPriorityId]  = useState(task?.priority?.priority_id ?? null);
  const [saving,      setSaving]      = useState(false);
  const [deleting,    setDeleting]    = useState(false);
  const [err,         setErr]         = useState('');

  const availableFolders = folders.filter(f => !folderIds.includes(f.folder_id));
  const selectedFolders  = folders.filter(f =>  folderIds.includes(f.folder_id));

  const addFolder    = id => setFolderIds(prev => [...prev, id]);
  const removeFolder = id => setFolderIds(prev => prev.filter(x => x !== id));

  const handleSave = async () => {
    setErr(''); setSaving(true);
    try {
      const data = { name, status, target_date: targetDate || null, description, notes, folder_ids: folderIds, priority_id: priorityId };
      const result = isNew ? await createTask(data) : await updateTask(task.task_id, data);
      onSave(result);
    } catch (e) { setErr(e.message); setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Permanently delete this task?')) return;
    setDeleting(true);
    try {
      await deleteTask(task.task_id);
      onSave(null, task.task_id);
    } catch (e) { setErr(e.message); setDeleting(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-card">
        <div className="modal-header">
          {task && <span className="task-ident-chip">{task.identifier || `T-${String(task.task_id).padStart(4,'0')}`}</span>}
          <h2>{isNew ? 'New Task' : 'Edit Task'}</h2>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {/* Name */}
          <div className="field">
            <label>Title</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Task title…" autoFocus={isNew} />
          </div>

          {/* Status + Date row */}
          <div className="field-row">
            <div className="field">
              <label>Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Target Completion Date</label>
              <input type="date" value={targetDate} onChange={e => setTargetDate(e.target.value)} />
            </div>
          </div>

          {/* Priority */}
          {priorities.length > 0 && (
            <div className="field">
              <label>Priority</label>
              <div className="priority-picker">
                {priorities.map(p => (
                  <button key={p.priority_id} type="button"
                    className={`priority-opt${priorityId === p.priority_id ? ' selected' : ''}`}
                    style={priorityId === p.priority_id ? tagStyle(p.color) : {}}
                    onClick={() => setPriorityId(priorityId === p.priority_id ? null : p.priority_id)}>
                    {p.name}
                  </button>
                ))}
                {priorityId && (
                  <button type="button" className="btn-ghost priority-clear"
                    onClick={() => setPriorityId(null)}>✕ Clear</button>
                )}
              </div>
            </div>
          )}

          {/* Folders */}
          <div className="field">
            <label>Folders</label>
            <div className="folders-picker">
              {selectedFolders.length > 0 && (
                <div className="folders-selected">
                  {selectedFolders.map(f => (
                    <span key={f.folder_id} className="folder-tag-remove" style={tagStyle(f.color)}>
                      {f.name}
                      <button type="button" className="rm"
                        onClick={e => { e.stopPropagation(); removeFolder(f.folder_id); }}>✕</button>
                    </span>
                  ))}
                </div>
              )}
              {availableFolders.length > 0 && (
                <div className="folders-add-row">
                  {availableFolders.map(f => (
                    <button key={f.folder_id} className="folder-add-btn" onClick={() => addFolder(f.folder_id)}>
                      + {f.name}
                    </button>
                  ))}
                </div>
              )}
              {selectedFolders.length === 0 && availableFolders.length === 0 && (
                <span style={{ fontSize: '0.82rem', color: 'var(--muted)' }}>No folders defined yet.</span>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="field">
            <label>Description</label>
            <RichTextEditor value={description} onChange={setDescription} placeholder="Description…" minHeight={100} />
          </div>

          {/* Notes */}
          <div className="field">
            <label>Notes</label>
            <RichTextEditor value={notes} onChange={setNotes} placeholder="Notes…" minHeight={80} />
          </div>

          {err && <div className="modal-err">{err}</div>}
        </div>

        <div className="modal-footer">
          {!isNew && (
            <button className="btn-danger" onClick={handleDelete} disabled={deleting || saving}>
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          <div className="spacer" />
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving || deleting}>
            {saving ? 'Saving…' : isNew ? 'Create Task' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Filter bar ────────────────────────────────────────────────────────────────
function FilterBar({ filters, folders, priorities, onChange, taskCount, onNewTask }) {
  const [folderOpen,   setFolderOpen]   = useState(false);
  const [priorityOpen, setPriorityOpen] = useState(false);
  const folderRef   = useRef(null);
  const priorityRef = useRef(null);

  useEffect(() => {
    const handler = e => {
      if (folderRef.current   && !folderRef.current.contains(e.target))   setFolderOpen(false);
      if (priorityRef.current && !priorityRef.current.contains(e.target)) setPriorityOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleStatus = s => {
    const next = filters.statuses.includes(s)
      ? filters.statuses.filter(x => x !== s)
      : [...filters.statuses, s];
    onChange({ ...filters, statuses: next });
  };

  const toggleFolder = id => {
    const next = filters.folder_ids.includes(id)
      ? filters.folder_ids.filter(x => x !== id)
      : [...filters.folder_ids, id];
    onChange({ ...filters, folder_ids: next });
  };

  const togglePriority = id => {
    const next = filters.priority_ids.includes(id)
      ? filters.priority_ids.filter(x => x !== id)
      : [...filters.priority_ids, id];
    onChange({ ...filters, priority_ids: next });
  };

  return (
    <div className="filter-bar">
      <button className="btn-primary filter-new-task" onClick={onNewTask}>+ New Task</button>

      <span className="filter-label">Status:</span>
      <div className="status-toggles">
        {STATUSES.map(s => (
          <button key={s}
            className={`status-toggle ${filters.statuses.includes(s) ? 'active' : ''} ${STATUS_TOGGLE_CLASS[s]}`}
            onClick={() => toggleStatus(s)}>{s}</button>
        ))}
      </div>

      <span className="filter-label" style={{ marginLeft: 6 }}>Due by:</span>
      <input type="date" className="filter-input filter-date"
        value={filters.before_date}
        onChange={e => onChange({ ...filters, before_date: e.target.value })} />

      {priorities.length > 0 && (
        <>
          <span className="filter-label">Priority:</span>
          <div className="folder-select" ref={priorityRef}>
            <button
              className={`folder-select-btn ${filters.priority_ids.length > 0 ? 'has-selection' : ''}`}
              onClick={() => setPriorityOpen(o => !o)}>
              {filters.priority_ids.length > 0 ? `${filters.priority_ids.length} selected` : 'All'}
              <span className="arrow">{priorityOpen ? '▲' : '▼'}</span>
            </button>
            {priorityOpen && (
              <div className="folder-dropdown">
                {priorities.map(p => (
                  <label key={p.priority_id} className="folder-dropdown-item">
                    <input type="checkbox" checked={filters.priority_ids.includes(p.priority_id)}
                      onChange={() => togglePriority(p.priority_id)} />
                    <span className="folder-dot" style={{ background: p.color }} />
                    <span className="folder-dropdown-name">{p.name}</span>
                  </label>
                ))}
                {filters.priority_ids.length > 0 && (
                  <div className="folder-dropdown-clear" onClick={() => onChange({ ...filters, priority_ids: [] })}>
                    Clear priority filter
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      <span className="filter-label">Folders:</span>
      <div className="folder-select" ref={folderRef}>
        <button
          className={`folder-select-btn ${filters.folder_ids.length > 0 ? 'has-selection' : ''}`}
          onClick={() => setFolderOpen(o => !o)}>
          {filters.folder_ids.length > 0 ? `${filters.folder_ids.length} selected` : 'All folders'}
          <span className="arrow">{folderOpen ? '▲' : '▼'}</span>
        </button>
        {folderOpen && (
          <div className="folder-dropdown">
            {folders.map(f => (
              <label key={f.folder_id} className="folder-dropdown-item">
                <input type="checkbox" checked={filters.folder_ids.includes(f.folder_id)}
                  onChange={() => toggleFolder(f.folder_id)} />
                <span className="folder-dot" style={{ background: f.color }} />
                <span className="folder-dropdown-name">{f.name}</span>
              </label>
            ))}
            {filters.folder_ids.length > 0 && (
              <div className="folder-dropdown-clear" onClick={() => onChange({ ...filters, folder_ids: [] })}>
                Clear folder filter
              </div>
            )}
          </div>
        )}
      </div>

      <input className="filter-input filter-search" placeholder="Search…"
        value={filters.search}
        onChange={e => onChange({ ...filters, search: e.target.value })} />

      <span className="filter-count">{taskCount} task{taskCount !== 1 ? 's' : ''}</span>
    </div>
  );
}

// ── Task table ────────────────────────────────────────────────────────────────
function TaskTable({ tasks, onSelectTask, setTooltip }) {
  const showTip = (e, html) => {
    const plain = stripHtml(html);
    if (!plain) return;
    setTooltip({ html, x: Math.min(e.clientX + 14, window.innerWidth - 440), y: e.clientY + 14 });
  };
  const hideTip = () => setTooltip(null);

  if (tasks.length === 0) {
    return <div className="no-tasks">No tasks match the current filters.</div>;
  }

  return (
    <table className="task-table">
      <thead>
        <tr>
          <th className="col-id">#</th>
          <th className="col-name">Title</th>
          <th className="col-desc">Description</th>
          <th className="col-status">Status</th>
          <th className="col-priority">Priority</th>
          <th className="col-date">Target</th>
          <th className="col-notes">Notes</th>
          <th className="col-folders">Folders</th>
        </tr>
      </thead>
      <tbody>
        {tasks.map(task => {
          const descPlain  = stripHtml(task.description);
          const notesPlain = stripHtml(task.notes);
          return (
            <tr key={task.task_id} className="task-row" onClick={() => onSelectTask(task)}>
              <td className="col-id">
                <span className="task-id">{task.task_id}</span>
              </td>
              <td className="col-name">
                <span className="cell-name">{task.name || <em style={{ color: 'var(--muted)' }}>Untitled</em>}</span>
              </td>
              <td className="col-desc">
                <span className={`cell-truncate ${descPlain ? 'has-content' : ''}`}
                  onMouseMove={e => showTip(e, task.description)}
                  onMouseLeave={hideTip}>
                  {descPlain || '—'}
                </span>
              </td>
              <td className="col-status">
                <span className={`status-badge ${STATUS_CLASS[task.status] || 'sb-new'}`}>
                  {task.status}
                </span>
              </td>
              <td className="col-priority">
                {task.priority
                  ? <span className="priority-badge" style={tagStyle(task.priority.color)}>{task.priority.name}</span>
                  : <span className="cell-empty">—</span>}
              </td>
              <td className="col-date">
                <span className={`cell-date ${isOverdue(task.target_date) ? 'overdue' : ''}`}>
                  {fmtDate(task.target_date) || '—'}
                </span>
              </td>
              <td className="col-notes">
                <span className={`cell-truncate ${notesPlain ? 'has-content' : ''}`}
                  onMouseMove={e => showTip(e, task.notes)}
                  onMouseLeave={hideTip}>
                  {notesPlain || '—'}
                </span>
              </td>
              <td className="col-folders">
                <div className="folder-tags">
                  {(task.folders || []).map(f => (
                    <span key={f.folder_id} className="folder-tag" style={tagStyle(f.color)}>{f.name}</span>
                  ))}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── App root ──────────────────────────────────────────────────────────────────
export default function App() {
  const [authed,     setAuthed]     = useState(false);
  const [pubkey,     setPubkey]     = useState('');
  const [checking,   setChecking]   = useState(true);

  const [tasks,      setTasks]      = useState([]);
  const [folders,    setFolders]    = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [loading,    setLoading]    = useState(false);

  const [filters, setFilters] = useState({
    statuses:     ['New', 'Started'],
    before_date:  '',
    folder_ids:   [],
    priority_ids: [],
    search:       '',
  });

  const [selectedTask,    setSelectedTask]    = useState(null);
  const [showFolders,     setShowFolders]     = useState(false);
  const [showPriorities,  setShowPriorities]  = useState(false);
  const [tooltip,         setTooltip]         = useState(null);

  useEffect(() => {
    async function init() {
      const params = new URLSearchParams(window.location.search);
      const ssoToken = params.get('sso');
      if (ssoToken) {
        try {
          const r = await fetch('/api/auth/sso', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: ssoToken }),
          });
          const d = await r.json();
          if (d.token) {
            localStorage.setItem('tasks:token', d.token);
            window.history.replaceState({}, '', window.location.pathname);
          }
        } catch {}
      }
      if (!getToken()) { setChecking(false); return; }
      authFetch('/api/auth/me')
        .then(r => r.json())
        .then(d => { if (d.pubkey) { setAuthed(true); setPubkey(d.pubkey); } })
        .catch(() => {})
        .finally(() => setChecking(false));
    }
    init();
  }, []);

  useEffect(() => {
    if (!authed) return;
    getFolders().then(setFolders).catch(console.error);
    getPriorities().then(setPriorities).catch(console.error);
  }, [authed]);

  const fetchTasks = useCallback(async (f) => {
    setLoading(true);
    try { setTasks(await getTasks(f)); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!authed) return;
    const t = setTimeout(() => fetchTasks(filters), filters.search ? 350 : 0);
    return () => clearTimeout(t);
  }, [authed, filters, fetchTasks]);

  const handleLogin = async () => {
    const d = await authFetch('/api/auth/me').then(r => r.json());
    setPubkey(d.pubkey); setAuthed(true);
  };

  const handleLogout = () => { logout(); setAuthed(false); setPubkey(''); setTasks([]); };

  const handleTaskSave = (updated, deletedId) => {
    if (deletedId) {
      setTasks(prev => prev.filter(t => t.task_id !== deletedId));
    } else if (updated) {
      setTasks(prev => {
        const exists = prev.some(t => t.task_id === updated.task_id);
        if (exists) return prev.map(t => t.task_id === updated.task_id ? updated : t);
        const next = [...prev, updated].sort((a, b) => {
          if (!a.target_date && !b.target_date) return a.task_id - b.task_id;
          if (!a.target_date) return 1;
          if (!b.target_date) return -1;
          return a.target_date.localeCompare(b.target_date) || a.task_id - b.task_id;
        });
        return next;
      });
    }
    setSelectedTask(null);
  };

  if (checking) return null;
  if (!authed)  return <LoginScreen onLogin={handleLogin} />;

  return (
    <div className="app">
      <header>
        <h1>Task Manager</h1>
        <button className="btn-ghost" style={{ fontSize: '0.82rem' }} onClick={() => setShowPriorities(true)}>
          Priorities
        </button>
        <button className="btn-ghost" style={{ fontSize: '0.82rem' }} onClick={() => setShowFolders(true)}>
          Folders
        </button>
        <span className="pubkey">{shortKey(pubkey)}</span>
        <button className="btn-ghost" style={{ fontSize: '0.82rem' }} onClick={handleLogout}>Logout</button>
      </header>

      <FilterBar
        filters={filters} folders={folders} priorities={priorities}
        onChange={setFilters} taskCount={tasks.length}
        onNewTask={() => setSelectedTask('new')}
      />

      {loading && <div style={{ padding: '4px 20px', fontSize: '0.82rem', color: 'var(--muted)' }}>Loading…</div>}

      <div className="task-wrap">
        <TaskTable tasks={tasks} onSelectTask={setSelectedTask} setTooltip={setTooltip} />
      </div>

      {tooltip && (
        <div className="task-tooltip"
          style={{ left: tooltip.x, top: tooltip.y }}
          dangerouslySetInnerHTML={{ __html: tooltip.html }} />
      )}

      {selectedTask && (
        <TaskModal
          key={selectedTask === 'new' ? 'new' : selectedTask.task_id}
          task={selectedTask === 'new' ? null : selectedTask}
          folders={folders} priorities={priorities}
          onSave={handleTaskSave}
          onClose={() => setSelectedTask(null)}
        />
      )}

      {showFolders && (
        <FolderModal folders={folders} onUpdate={setFolders} onClose={() => setShowFolders(false)} />
      )}

      {showPriorities && (
        <PriorityModal priorities={priorities} onUpdate={setPriorities} onClose={() => setShowPriorities(false)} />
      )}
    </div>
  );
}
